import {
  getLocalBasketDataForVendor,
  setLocalBasketDataForVendor,
} from '@/components/page-specific/catering/common/components/states/local-basket'

import { useCallback, useEffect, useState } from 'react'

import { SelectorBasket } from '.'
import {
  useBasketSelectionActions,
  useBasketSelectionState,
} from '../../store/basketSelectionStore'

type Props = {
  vendorId?: string
}

export const useUnauthenticatedUserBasketSelection = ({ vendorId }: Props) => {
  const [called, setCalled] = useState(false)
  const { unauthenticatedBaskets } = useBasketSelectionState()
  const { setUnauthenticatedBaskets } = useBasketSelectionActions()

  const onCreate = useCallback(
    (newBasket: SelectorBasket) => {
      if (!vendorId) return
      setUnauthenticatedBaskets((prev) => {
        // map used to prevent any duplicate baskets being created when fetching existing ones
        const unauthedBaskets = new Map<string, SelectorBasket>()

        for (const basket of prev) {
          unauthedBaskets.set(basket.id, basket)
        }
        unauthedBaskets.set(newBasket.id, newBasket)

        const newBaskets = Array.from(unauthedBaskets.values())
        setLocalBasketDataForVendor(vendorId, newBaskets)
        return newBaskets
      })
    },
    [vendorId, setUnauthenticatedBaskets],
  )

  const onDelete = useCallback(
    (basketId: string) => {
      if (!vendorId) return
      setUnauthenticatedBaskets((prev) => {
        const filteredBaskets = prev.filter((basket) => basketId !== basket.id)
        setLocalBasketDataForVendor(vendorId, filteredBaskets)
        return filteredBaskets
      })
    },
    [vendorId, setUnauthenticatedBaskets],
  )

  const update = useCallback(
    (updatedBasketData: SelectorBasket) => {
      if (!vendorId) return
      setUnauthenticatedBaskets((prev) => {
        const updatedBaskets = prev.map((existingBasket) =>
          existingBasket.id === updatedBasketData.id ? updatedBasketData : existingBasket,
        )
        setLocalBasketDataForVendor(vendorId, updatedBaskets)
        return updatedBaskets
      })
    },
    [vendorId, setUnauthenticatedBaskets],
  )

  useEffect(() => {
    if (!vendorId) return
    const localBaskets = getLocalBasketDataForVendor(vendorId)
    if (Array.isArray(localBaskets)) {
      setUnauthenticatedBaskets(localBaskets)
    } else if (localBaskets) {
      setUnauthenticatedBaskets([{ id: localBaskets }])
    } else {
      setUnauthenticatedBaskets([])
    }
    setCalled(true)
  }, [vendorId, setUnauthenticatedBaskets])

  return {
    baskets: unauthenticatedBaskets,
    called,
    onCreate,
    onDelete,
    update,
  }
}
